import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import { AUTH_EVENT, AUTH_METHOD, authEventProps } from "../authAnalytics";

// `detail` is a plain string, or a UIVisibleError object whose `message` is a translation key.
type UIVisibleErrorDetail = { message: string; error_type: "UIVisibleError" };
interface ErrorResponse {
	detail?: string | UIVisibleErrorDetail;
}

// Translate a UIVisibleError key, pass a plain string through, else undefined so the caller falls back.
const resolveDetailMessage = (detail: string | UIVisibleErrorDetail | undefined, t: TFunction): string | undefined => {
	if (typeof detail === "string") return detail;
	if (detail?.error_type === "UIVisibleError" && detail.message) return t(detail.message);
	return undefined;
};

// Signin error message: offline → network message; else the route's detail, else `fallback`
// (already translated, so callers can pass interpolated strings).
export const resolveSigninErrorMessage = (error: AxiosError<ErrorResponse>, fallback: string, t: TFunction): string =>
	!error.response ? t("error.no.internet.connection") : resolveDetailMessage(error.response.data?.detail, t) || fallback;

// The OTP-login rate limiter (slowapi) returns 429 when too many wrong codes are tried for a user.
const isOtpRateLimited = (error: AxiosError<ErrorResponse>): boolean => error.response?.status === 429;

// Backend signal (403) that a support user tried OTP and must use password login instead.
const OTP_NOT_ALLOWED_FOR_SUPPORT = "otp_not_allowed_for_support";
export const isSupportOtpBlocked = (error: unknown): boolean =>
	!!error &&
	typeof error === "object" &&
	(error as AxiosError<ErrorResponse>).response?.status === 403 &&
	(error as AxiosError<ErrorResponse>).response?.data?.detail === OTP_NOT_ALLOWED_FOR_SUPPORT;

interface CheckUsernameResult {
	user_exists: boolean;
	is_initialized: boolean;
	has_valid_pending_setup: boolean;
	setup_id?: number;
}

const useShowErrorSnackbar = () => {
	const { trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();

	return (message: string, trackErrorData: Record<string, string | number | boolean | null | undefined>) => {
		trackError(message, trackErrorData);
		showSnackbar(message, "error");
	};
};

export const useCheckUsernameStatus = () => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const showErrorSnackbar = useShowErrorSnackbar();

	return useMutation<CheckUsernameResult, AxiosError<ErrorResponse>, string>({
		mutationFn: async (username: string) => {
			track("username check", { type: "username", status: "start", email: username });
			const response = await backendService.checkUsernameStatus(username);
			return response.data;
		},
		onSuccess: (result, username) => {
			if (!result.user_exists) {
				showErrorSnackbar(t("error.user.not.found"), {
					type: "username",
					status: "user not found",
					email: username,
				});
			} else if (result.is_initialized) {
				track("username check", { type: "username", status: "existing user", email: username });
			} else if (result.has_valid_pending_setup) {
				track("username check", { type: "username", status: "first login with setup", email: username });
			} else {
				track("username check", { type: "username", status: "first login without setup", email: username });
			}
		},
		onError: (error: AxiosError<ErrorResponse>, username) => {
			const message = resolveSigninErrorMessage(error, t("error.checking.username"), t);
			showErrorSnackbar(message, {
				code: "unknown",
				action: "username check failed",
				username,
			});
		},
	});
};

export const useSendResetPassword = () => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const showErrorSnackbar = useShowErrorSnackbar();

	return useMutation<void, AxiosError<ErrorResponse>, string>({
		mutationFn: async (username: string) => {
			await backendService.sendNurseReset({ emailOrPhoneNumber: username, sendFromMultipleCommunications: true });
		},
		onSuccess: () => {
			showSnackbar(t("reset.password.email.sent"), "success");
		},
		onError: (error: AxiosError<ErrorResponse>, username) => {
			const message = resolveSigninErrorMessage(error, t("error.sending.reset.password"), t);
			showErrorSnackbar(message, {
				action: "send reset password failed",
				username,
			});
		},
	});
};

export const useSendOtp = () => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const showErrorSnackbar = useShowErrorSnackbar();

	return useMutation<void, AxiosError<ErrorResponse>, string>({
		mutationFn: async (username: string) => {
			track("send otp", { type: "otp", status: "start", email: username });
			await backendService.verifyEmail(username);
		},
		onSuccess: (_result, username) => {
			showSnackbar(t("otp.resent.success", { username }), "success");
		},
		onError: (error: AxiosError<ErrorResponse>, username) => {
			// Support-user block is not an error to the user — the caller redirects to password login. Stay quiet.
			if (isSupportOtpBlocked(error)) {
				return;
			}
			const message = resolveSigninErrorMessage(error, t("otp.resent.error", { username }), t);
			showErrorSnackbar(message, {
				action: "send otp failed",
				username,
			});
		},
	});
};

interface VerifyOtpLoginVariables {
	username: string;
	code: string;
}

export const useVerifyOtpLogin = ({ onLocked }: { onLocked?: () => void } = {}) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { saveTokenData } = useAuth();
	const navigate = useNavigate();
	const showErrorSnackbar = useShowErrorSnackbar();

	return useMutation<void, AxiosError<ErrorResponse>, VerifyOtpLoginVariables>({
		mutationFn: async ({ username, code }: VerifyOtpLoginVariables) => {
			track("verify otp login", { type: "otp", status: "start", email: username });
			// Backend skips MFA for OTP login (the code already proves possession), so the
			// response always carries an access token — no /mfa branch needed here.
			const response = await backendService.verifyOtpLogin({ username, code });

			const { isSuccessful } = await saveTokenData(response.data);
			if (!isSuccessful) {
				throw new Error(t("could.not.verify.one.time.password"));
			}

			track("verify otp login", { type: "otp", status: "success", email: username });
			// OTP-specific event + unified login funnel (an OTP verify is also a login).
			track(AUTH_EVENT.OTP_VERIFICATION_SUCCESS, authEventProps(AUTH_METHOD.OTP, true));
			track(AUTH_EVENT.LOGIN_SUCCESS, authEventProps(AUTH_METHOD.OTP, true));
			navigate("/");
		},
		onError: (error: AxiosError<ErrorResponse>, variables) => {
			// Too many wrong codes → show the temporary-lock screen instead of a snackbar.
			if (isOtpRateLimited(error)) {
				track("verify otp login", { type: "otp", status: "locked", email: variables.username });
				trackError(AUTH_EVENT.OTP_VERIFICATION_FAILURE, { ...authEventProps(AUTH_METHOD.OTP, false), reason: "rate_limited" });
				trackError(AUTH_EVENT.LOGIN_FAILED, { ...authEventProps(AUTH_METHOD.OTP, false), reason: "rate_limited" });
				onLocked?.();
				return;
			}

			trackError(AUTH_EVENT.OTP_VERIFICATION_FAILURE, authEventProps(AUTH_METHOD.OTP, false));
			trackError(AUTH_EVENT.LOGIN_FAILED, authEventProps(AUTH_METHOD.OTP, false));
			const message = resolveSigninErrorMessage(error, t("could.not.verify.one.time.password"), t);
			showErrorSnackbar(message, {
				action: "verify otp login failed",
				username: variables.username,
			});
		},
	});
};

interface ResetPasswordFromAppVariables {
	username: string;
	password: string;
}

export const useResetPasswordFromApp = () => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { login } = useAuth();
	const navigate = useNavigate();
	const showErrorSnackbar = useShowErrorSnackbar();

	return useMutation<unknown, AxiosError<ErrorResponse>, ResetPasswordFromAppVariables>({
		mutationFn: async ({ username, password }: ResetPasswordFromAppVariables) => {
			track("set password", { type: "set-password", status: "start", email: username });

			await backendService.resetPasswordFromApp({
				newPassword: password,
				username,
			});

			showSnackbar(t("password.set.successfully"), "success");
			track(AUTH_EVENT.PASSWORD_RESET_COMPLETED, authEventProps(AUTH_METHOD.PASSWORD, true));

			// Now log in with the new password
			const result = await login(username, password);

			if (result.isSuccessful) {
				track("login after set password", {
					type: "password",
					status: "success",
					email: username,
				});
				track(AUTH_EVENT.LOGIN_SUCCESS, authEventProps(AUTH_METHOD.PASSWORD, true));
				navigate("/");
			} else {
				showErrorSnackbar("login after set password", {
					type: "password",
					status: "fail",
					email: username,
				});
				trackError(AUTH_EVENT.LOGIN_FAILED, authEventProps(AUTH_METHOD.PASSWORD, false));
				navigate("/login");
			}

			return result;
		},
		onError: (error: AxiosError<ErrorResponse>, variables) => {
			trackError(AUTH_EVENT.PASSWORD_RESET_COMPLETED, { ...authEventProps(AUTH_METHOD.PASSWORD, false), reason: "set_password_failed" });
			const message = resolveSigninErrorMessage(error, t("error.setting.password"), t);
			showErrorSnackbar(message, {
				code: "unknown",
				action: "set password failed",
				username: variables.username,
			});
		},
	});
};
